/* eslint no-eval: 0 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { DraggableHeaderRenderer } from './HeaderRenderers/DraggableHeaderRenderer';
import DataGrid from 'react-data-grid';
import type { HeaderRendererProps } from 'react-data-grid';
import 'react-data-grid/lib/styles.css';
import './jobs/jobs.css';
import { PaginationDetails } from './pagination_details';
import { ReactComponent as ClearAllFilterIcon } from '../assets/clearAllFilter.svg';
import { ReactComponent as DefaultViewIcon } from '../assets/default-view.svg';
import { ReactComponent as ClearSortIcon } from '../assets/clear-sort.svg';
import { ReactComponent as ExportCSVIcon } from '../assets/PepWise2.0/Export-CSV.svg';
import { ReactComponent as ExportXLSXIcon } from '../assets/PepWise2.0/Export-XLSX.svg';
import { ReactComponent as MoreIconRounded } from '../assets/PepWise2.0/MoreIconRounded.svg';

import MoreOptionDropdown, { MenuItemType } from './Dropdown/MoreOption';
import { Box, debounce, Stack, Typography, StackProps, Grid, Button } from '@mui/material';
import LoaderComponent from './Loader';
import ColumnSelectorDialog from './HeaderRenderers/ColumnSelectorDialog';
import InfoIcon from '@mui/icons-material/Info';
import DialogueModal from 'components/Modal/DialogModal';
import { useTheme } from "@mui/material/styles";
import {GroupByDropdown} from 'components/Common/GroupByDropdown';
import { UseColumnFiltersResult } from './ColumnFilters/useColumnFilters';

type EmptyRowsRendererProps = {
	isFetching: boolean;
	textColor: any;
	tableType: any;
};

export function EmptyRowsRenderer({
	isFetching,
	textColor,
	tableType
}: EmptyRowsRendererProps) {
	const isReconciliationTable = tableType?.toLowerCase().startsWith('data-reconciliation-');
	if (isFetching) {
		return null;
	}

	return (
		<Box
			width="100%"
			display="flex"
			alignItems="center"
			justifyContent="center"
			textAlign="center"
			className="records-msg"
			sx={{
				position: 'absolute',
				top: isReconciliationTable ? '44px' : 0,
				left: 0,
				right: 0,
				bottom: 0,
				color: `${textColor} !important`,
			}}
			p={2}
		>
			No records found
		</Box>
	);
}

type CustomizedDataGridProps = Record<string, any> & {
	children?: React.ReactNode;
	childrenContainerProps?: StackProps;
	noRowsFallback?: React.ReactNode;
	enableColumnFilter?: boolean;
  	columnFilterController?: UseColumnFiltersResult;
	getColumnFilterValues?: (field: string) => Promise<any[]>;
	toolbarSx?: any;
};

export function CustomizedDataGrid({
	tableColumns,
	tableData,
	tableType,
	onCellEdit,
	defaultColumnNames,
	onSorting,
	currSorting,
	filterDataOptions,
	searchDataValues,
	onFilterCheck,
	currFilter,
	onFilterSearch,
	onHeaderSelectAll,
	onFilterClick,
	showExport,
	isShowDisabledButton,
	fileName,
	tableName,
	formatType,
	exportColumns,
	exportTableData,
	exportTableDataCSV,
	page,
	pageSize,
	paginationData,
	listLength,
	isTotalCountLoading = false,
	handlepageSizeChange,
	handlePageChange,
	showGoToPage,
	isFetching,
	isDateRangeFilter,
	isDisabled,
	isGroupBy=false,
	onGroupByChange,
	groupBy,
	groupByList=[],
	onCellClick,
	selectedRowId,
	isShowMoreButton = true,
	idleFilterIcon,
	title,
	rowHeight,
	headerRowHeight,
	autoSetDraggedColumns = true,
	forceVisibleColumnNames = [],
	isRenderPagination = true,
	isPrevNextPagination = false,
	isEditing = false,
	style,
	sectorsList,
	gridRef,
	childRowClass,
	isFinopsProgramsList = false,
	isLighterTheme = false,
	headerCheckboxRef,
	children,
	childrenContainerProps,
	headerCheckbox,
	isSortIcon = false,
	isAllocationModal = false,
	isAllocationModalSLA = false,
	pageSizeOptions,
	iconColor,
	hierarchyRender,
	dropdownOptions,
	dropdownValue,
	onDropdownChange,
	includeGroupByMenuColumns,
	onMetadataUpdatesClick,
	metadataButtonLabel,
	noRowsFallback,
	rowClass,
	isChildren,
	enableColumnFilter = false,
  	columnFilterController,
	getColumnFilterValues,
	toolbarSx,
}: CustomizedDataGridProps) {
	const theme = useTheme();
	const [windowSize, setWindowSize] = useState(window.innerWidth);
	const gridViewportRef = useRef<HTMLDivElement>(null);
	type Row = (typeof tableData)[0];
	const [columns, setColumns] = useState(tableColumns);
	const [sortColumns, setSortColumns] = useState<any[]>([]);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [tempVisibleColumns, setTempVisibleColumns] = useState<string[]>([]);
	const [selectAllColumns, setSelectAllColumns] = useState(false);
	const [selectInfoModel, setInfoModel] = useState(false);

	useEffect(() => {
		setSelectAllColumns(tempVisibleColumns.length === tableColumns.length);
	}, [tempVisibleColumns]);

	const handleOpenColumnSelector = (selectedCols: string[]) => {
		setTempVisibleColumns(selectedCols);
		setSelectAllColumns(selectedCols.length === tableColumns.length);
		setDialogOpen(true);
	};

	const handleCloseColumnSelector = () => setDialogOpen(false);

	const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		setSelectAllColumns(checked);
		setTempVisibleColumns(checked ? tableColumns.map((col: any) => col.name) : [tableColumns[0].name, "HeaderCheckboxMapping", "Actions"]);
	};

	const handleColumnChange = (col: string, e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		setTempVisibleColumns(prev => {
			let updated = checked ? [...prev, col] : prev.filter(c => c !== col);
			const tableOrder = tableColumns.map((c: any) => c.name); // Always sort according to tableColumns order
			updated = tableOrder.filter((name: any) => updated.includes(name));
			return updated;
		});
	};

	const handleSaveColumns = () => {
		const updatedCols = tableColumns.filter((col: any) => tempVisibleColumns.includes(col.name));
		setColumns(updatedCols);
		localStorage.setItem(`${tableType}_filterPopupOpen`, 'true');
		localStorage.setItem(`${tableType}_user_col_pref`, JSON.stringify(tempVisibleColumns));
		window.dispatchEvent(new StorageEvent('storage', { key: `${tableType}_user_col_pref`, newValue: JSON.stringify(tempVisibleColumns) }));
		setDialogOpen(false);
	};

	const handleCancelColumns = () => setDialogOpen(false);

	useEffect(() => {
		if (isAllocationModal)
			setColumns(tableColumns)
	}, [tableColumns]);

	useEffect(() => {
		if (isAllocationModalSLA)
			setColumns(tableColumns)
	}, [tableColumns]);

	useEffect(() => {

	}, [tableData]);

	useEffect(() => {
		const handleResize = debounce(() => {
			setWindowSize(window.innerWidth);
		}, 200);

		handleResize();
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const sortedRows = useMemo((): Row[] => {
		if (sortColumns.length === 0) return tableData;

		return [...tableData].sort((a, b) => {
			for (const sort of sortColumns) {
				const comparator = getComparator(sort.columnKey);
				const compResult = comparator(a, b);
				if (compResult !== 0) {
					return sort.direction === 'ASC' ? compResult : -compResult;
				}
			}
			return 0;
		});
	}, [tableData, sortColumns]);

	type Comparator = (a: Row, b: Row) => number;
	function getComparator(sortColumn: string): Comparator {
		return (a: any, b: any) => {
			return a[sortColumn].localeCompare(b[sortColumn]);
		};
	}

	const resolvedGroupByList = useMemo(() => {
		if (Array.isArray(groupByList) && groupByList.length > 0) {
			return groupByList;
		}

		return tableColumns.map((column: any) => ({
			value: column.key,
			label: column.name
	}));
	}, [groupByList, tableColumns]);

	const draggableColumns = useMemo(() => {
		function headerRenderer(props: HeaderRendererProps<Row>) {
			return (
				<DraggableHeaderRenderer
					{...props}
					headerCheckbox={headerCheckbox}
					onColumnsReorder={handleColumnsReorder}
					onContextMenu={handleContextClick}
					onFilterMenu={handleFilterClick}
					filterData={filterDataOptions}
					searchData={searchDataValues}
					onFilterCheck={handleFilterValues}
					onFilterSearch={handleSearchValues}
					onHeaderSelectAll={handleHeaderSelectAll}
					onFilterClick={handleAndGetFilterOptions}
					columnNames={tableColumns.map((col: any) => {
						return col.name;
					})}
					visibleColumns={columns.map((col: any) => {
						return col.name;
					})}
					currSorting={currSorting}
					currFilter={currFilter}
					tableColumnKeys={tableColumns.map((col: any) => {
						return col.key;
					})}
					tableType={tableType}
					sectorsList={sectorsList}
					isSortIcon={isSortIcon}
					idleFilterIcon={idleFilterIcon}
					hierarchyRender={hierarchyRender}
					dropdownOptions={dropdownOptions}
					dropdownValue={dropdownValue}
					onDropdownChange={onDropdownChange}
					enableColumnFilter={enableColumnFilter}
					columnFilterController={columnFilterController}
					getColumnFilterValues={getColumnFilterValues}
					columnFilterColumns={tableColumns}
					columnFilterRows={tableData}
				/>
			);
		}
		function handleContextClick(selectedColumns: any) {
			handleOpenColumnSelector(selectedColumns);
			const currColKeys = columns.map((col: any) => {
				return selectedColumns.includes(col.name) ? col.name : null;
			});
			const currColumns = columns.filter((col: any) => {
				return selectedColumns.includes(col.name);
			});
			tableColumns.map((col: any) => {
				if (selectedColumns.includes(col.name) && !currColKeys.includes(col.name)) {
					currColumns.push(col);
				}
				return null;
			});
			setColumns(currColumns);
			const colPrefValue = JSON.stringify(currColumns.map((col: any) => col.name));
			localStorage.setItem(`${tableType}_user_col_pref`, colPrefValue);
			window.dispatchEvent(new StorageEvent('storage', { key: `${tableType}_user_col_pref`, newValue: colPrefValue }));
		}
		function handleFilterClick(sortingInfo: any) {
			onSorting(sortingInfo);
		}
		function handleFilterValues(filterInfo: any, columnKey: any) {
			if (typeof onFilterCheck === 'function') {
				onFilterCheck(filterInfo, columnKey);
			}
		}
		function handleSearchValues(searchDetails: any, columnKey: any) {
			onFilterSearch(searchDetails, columnKey);
		}
		function handleHeaderSelectAll(event: any) {
			onHeaderSelectAll(event);
		}
		function handleAndGetFilterOptions(column: any) {
			onFilterClick(column);
		}
		function handleColumnsReorder(sourceKey: string, targetKey: string) {
			const sourceColumnIndex = columns.findIndex((c: any) => c.key === sourceKey);
			const targetColumnIndex = columns.findIndex((c: any) => c.key === targetKey);
			const reorderedColumns = [...columns];
			reorderedColumns.splice(targetColumnIndex, 0, reorderedColumns.splice(sourceColumnIndex, 1)[0]);
			setColumns(reorderedColumns);
			localStorage.setItem(`${tableType}_user_col_pref`, JSON.stringify(reorderedColumns.map((col: any) => col.name)));
		}
		return columns.map((c: any) => {
			if (c.key === 'id') return c;
			return { ...c, headerRenderer };
		});
	}, [columns, currSorting, filterDataOptions, currFilter, searchDataValues]);

	function handleEditCell(rows: Row[], index: any) {
		onCellEdit(rows, index);
	}

	function getPreferredCols() {
		const storedPref: any = localStorage.getItem(`${tableType}_user_col_pref`);
		let storedColumnNames: any = storedPref === 'undefined' ? '' : JSON.parse(storedPref);
		const allCurrentColumnNames = tableColumns.map((col: any) => col.name);
		const hasNewColumns = storedColumnNames &&
			Array.isArray(storedColumnNames) &&
			allCurrentColumnNames.some((colName: any) => !storedColumnNames.includes(colName));
		const isFilterPopupOpen = localStorage.getItem(`${tableType}_filterPopupOpen`) === 'true';
		if (hasNewColumns && !isFilterPopupOpen) {
			console.log('New columns detected, clearing stored preference');
			localStorage.removeItem(`${tableType}_user_col_pref`);
			storedColumnNames = null;
		} 
		return storedColumnNames?.length ? storedColumnNames : defaultColumnNames;
	}

	useEffect(() => {
		const isGroupingActive = groupBy && groupBy !== 'none';
		if (autoSetDraggedColumns && !isGroupingActive) {
			let preferredColumnNames = [...getPreferredCols()];
			forceVisibleColumnNames.forEach((name: string) => {
				const columnIndex = tableColumns.findIndex((column: any) => column.name === name);
				if (columnIndex === -1) return;

				preferredColumnNames = preferredColumnNames.filter((columnName: string) => columnName !== name);
				const previousColumnName = tableColumns[columnIndex - 1]?.name;
				const previousColumnIndex = preferredColumnNames.indexOf(previousColumnName);
				preferredColumnNames.splice(
					previousColumnIndex >= 0 ? previousColumnIndex + 1 : preferredColumnNames.length,
					0,
					name,
				);
			});
			// Append filltered columnNames

			if (currFilter?.atRisk) preferredColumnNames.push('Threshold %');
			currFilter &&
				Object.keys(currFilter).forEach((key: any) => {
					if (currFilter[key]?.length) {
						const filteredColumnName = tableColumns.filter((col: any) => col.key === key)[0]?.name;
						if (!preferredColumnNames.includes(filteredColumnName)) preferredColumnNames.push(filteredColumnName);
					}
				});
			if (preferredColumnNames) {
				const preferredColumns = tableColumns.filter((col: any) => {
					return preferredColumnNames.includes(col.name);
				});
				preferredColumns.sort(
					(a: any, b: any) => preferredColumnNames.indexOf(a.name) - preferredColumnNames.indexOf(b.name)
				);
				setColumns(preferredColumns);
				if (filterDataOptions?.thresholdPercent) {
 					 filterDataOptions.thresholdPercent = filterDataOptions.thresholdPercent.map(String);
				}
			}
		} else if (isGroupingActive) {
			setColumns(tableColumns);
		}
	}, [tableColumns, groupBy, forceVisibleColumnNames]);

	useEffect(() => {
		const frameId = window.requestAnimationFrame(() => {
			const grid = gridViewportRef.current?.querySelector<HTMLElement>('.rdg');
			if (grid) grid.scrollLeft = 0;
		});

		return () => window.cancelAnimationFrame(frameId);
	}, [groupBy]);

	function enableDefaultViewBtn() {
		const setDefaultColumnNames = defaultColumnNames.filter((value: any) => value != null);
		if (setDefaultColumnNames)
			return JSON.stringify(setDefaultColumnNames) !== JSON.stringify(draggableColumns.map((x: any) => x.name));
		else return false;
	}

	const resetView = (event: any) => {
		if (defaultColumnNames) {
			const defaultColumns = tableColumns.filter((col: any) => {
				return defaultColumnNames.includes(col.name);
			});
			// Sort columns to match defaultColumnNames order so the
			// enableDefaultViewBtn comparison (JSON.stringify) will match
			defaultColumns.sort(
				(a: any, b: any) => defaultColumnNames.indexOf(a.name) - defaultColumnNames.indexOf(b.name)
			);
			setColumns(defaultColumns);
			localStorage.removeItem(`${tableType}_user_col_pref`);
		}
		localStorage.setItem(`${tableType}_filterPopupOpen`, 'false');
	};

	function enableClearFilter() {
		let enableClear = false;
		if (!currFilter) return false;
		Object.keys(currFilter).forEach((key: any) => {
			if (currFilter[key] && currFilter[key].length) {
				enableClear = true;
				return false;
			}
		});
		return enableClear;
	}

	const handleClearAllFilter = (event: any = null) => {
		const filterQuerySkeleton = {} as any;
		tableColumns.forEach((col: any) => {
			filterQuerySkeleton[col.key] = [];
		});
		onFilterCheck(filterQuerySkeleton, '');
	};

	useEffect(() => {
		const isDrillDownExecution = localStorage.getItem('drill-down-execution');
		const isDrillDownJobs = localStorage.getItem('drill-down-jobs');
		const isDrillDownSchdeule = localStorage.getItem('drill-down-schedules');
		if (isDrillDownExecution === 'false') {
			handleClearAllFilter();
			localStorage.removeItem('drill-down-execution');
		} else if (isDrillDownJobs === 'false') {
			handleClearAllFilter();
			localStorage.removeItem('drill-down-jobs');
		} else if (isDrillDownSchdeule === 'false') {
			handleClearAllFilter();
			localStorage.removeItem('drill-down-schedules');
		}
	}, []);
	function enableClearSoting() {
		return currSorting?.sortBy && currSorting?.sortBy?.length;
	}

	const handleClearSorting = (event: any) => {
		if (columnFilterController) {
			columnFilterController.clearSort();
		}
		onSorting({ sortBy: '', sortDir: '' });
	};

	const handleCellClick = (args: any, event: any) => {
		onCellClick && onCellClick(args.row);
	};

	const menuItems: MenuItemType[] = [
		{
			key: 'defaultView',
			label: 'Default View',
			icon: <DefaultViewIcon />,
			onClick: resetView,
			disabled: !enableDefaultViewBtn()
		},
		...(tableType === 'metadata-updates' || tableType === 'bulk-upload'
			? []
			: [{
				key: 'ClearAllFilter',
				label: 'Clear All Filter',
				icon: <ClearAllFilterIcon />,
				onClick: handleClearAllFilter,
				disabled: !enableClearFilter()
			}] as MenuItemType[]),
		...(tableType === 'bulk-upload'
			? []
			: [{
				key: 'ClearSorting',
				label: 'Clear Sorting',
				icon: <ClearSortIcon />,
				onClick: handleClearSorting,
				disabled: !enableClearSoting()
			}] as MenuItemType[]),
		...(exportTableDataCSV
			? ([
				{
				key: 'exportCSV',
				label: 'Export in CSV',
				icon: <ExportCSVIcon />,
				onClick: async () => {
					const storedPref: any = localStorage.getItem(`${tableType}_user_col_pref`);
					const storedColumnNames: any = storedPref ? JSON.parse(storedPref) : null;
					const preferredColumnNames =
					storedColumnNames && storedColumnNames.length ? storedColumnNames : defaultColumnNames;

					await exportTableDataCSV(preferredColumnNames);
				},
				isExportButton: true,
				isShowExportButton: !!showExport
				}
			] as MenuItemType[])
			: []),
		{
			key: 'exportXLSX',
			label: 'Export in XLSX',
			icon: <ExportXLSXIcon />,
			onClick: async () => {
				const storedPref: any = localStorage.getItem(`${tableType}_user_col_pref`);
				const storedColumnNames: any = JSON.parse(storedPref);
				const preferredColumnNames = storedColumnNames && storedColumnNames.length ? storedColumnNames : defaultColumnNames;
				await exportTableData(preferredColumnNames);
			},
			isExportButton: true,
			isShowExportButton: !!showExport,
		}
	];

	const exactPathsExcluded = [
		'/tableau/summary/dashboard',
		'/tableau/performance/dashboard',
		'/tableau/governance/dashboard'
	];

	const partialPathsExcluded = [
		'/tableau/observation',
		'/presto/dashboard',
		'/presto/observation',
		'/databricks/observation',
		'/dataGrowth/observation',
		'/data-pipeline/programOps',
		//'/finops/details',
		'data-pipeline/search-jobs',
		// '/finops/resourcemapping/programs',
	];

	const isIbpTableType = typeof tableType === 'string' && tableType.toLowerCase().startsWith('ibp');
	const isShowMoreOption = !(
		(
			exactPathsExcluded.includes(location.pathname) || // exact match
			partialPathsExcluded.some((path) => location.pathname.includes(path))
		) // partial match
	) || isIbpTableType;
	const handleInfoClick = () => {
		setInfoModel(true);
	}
	return (
		<>
			<Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} mb={1}>
				<Typography variant="subHeader" sx={{ whiteSpace: 'nowrap', color: `${theme.palette.table.title}` }} >{title ?? ''}</Typography>
				{
					(tableType === 'Pipeline' || tableType === 'Platform' || tableType === 'BI Ops' || tableType === "AI/ML Ops") && (
						<InfoIcon style={{ fontSize: '16px', marginLeft: '10px' }} onClick={handleInfoClick} />
					)
				}
				<DialogueModal
					title={'KPI Calculations'}
					isOpen={selectInfoModel}
					handleClose={() => {
						setInfoModel(false);
					}}
					className='info-model'
				>
					<Grid container spacing={1} marginTop="1rem" px={{ xs: 1, md: 2, lg: 3 }}>
						<Grid item xs={12} md={12} lg={12} style={{ background: '#003771', borderRadius: '4px', textAlign: 'left' }}>
							<p style={{ fontSize: '14px', float: 'left', marginLeft: '10px' }}><span style={{ fontSize: '1rem', fontWeight: 'bold', marginRight: 10 }}>Incident Volume :</span>Count of incidents per technology in the month selected. Considering only the incidents not created by System or opened by Event Management.</p>
						</Grid>
						<Grid item xs={12} md={12} lg={12} style={{ background: '#003771', borderRadius: '4px', textAlign: 'left' }}>
						rowClass={rowClass}	
							<p style={{ fontSize: '14px', float: 'left', marginLeft: '10px' }}><span style={{ fontSize: '1rem', fontWeight: 'bold', marginRight: 10 }}>Response SLA :</span>Calculated as percentage of incidents per technology in the selected month, which did not breach the SLA against the total count of incidents. <br /><br /><b>Conditions:</b> SLA Type is SLA, SLA Stage is one of Paused or Completed, Incident state is one of Closed or Resolved, SLA Target is Response.</p>
						</Grid>
						<Grid item xs={12} md={12} lg={12} style={{ background: '#003771', borderRadius: '4px', textAlign: 'left' }}>
							<p style={{ fontSize: '14px', float: 'left', marginLeft: '10px' }}><span style={{ fontSize: '1rem', fontWeight: 'bold', marginRight: 10 }}>Resolution SLA :</span>Calculated as percentage of incidents per technology in the selected month, which did not breach the SLA against the total count of incidents. <br /><br /><b>Conditions:</b> SLA Type is SLA, SLA Stage is one of Paused or Completed, Incident state is one of Closed or Resolved, SLA Target is Resolution.</p>
						</Grid>
						<Grid item xs={12} md={12} lg={12} style={{ background: '#003771', borderRadius: '4px', textAlign: 'left' }}>
							<p style={{ fontSize: '14px', float: 'left', marginLeft: '10px' }}><span style={{ fontSize: '1rem', fontWeight: 'bold', marginRight: 10 }}>MTTR :</span>Calculated using the column Business Duration in Incidents table. Average of Business Duration for all the incidents by technology in the month selected.</p>
						</Grid>
						<Grid item xs={12} md={12} lg={12} style={{ background: '#003771', borderRadius: '4px', textAlign: 'left' }}>
							<p style={{ fontSize: '14px', float: 'left', marginLeft: '10px' }}><span style={{ fontSize: '1rem', fontWeight: 'bold', marginRight: 10 }}>Aged Incidents :</span>Count of incidents per technology which are not in one of Resolved, Closed or Canceled state and older than 7 days.</p>
						</Grid>
						<Grid item xs={12} md={12} lg={12} style={{ background: '#003771', borderRadius: '4px', textAlign: 'left' }}>
							<p style={{ fontSize: '14px', float: 'left', marginLeft: '10px' }}><span style={{ fontSize: '1rem', fontWeight: 'bold', marginRight: 10 }}>Aged SRs :</span>Count of Service requests per technology which are not in one of Closed Complete or Closed Incomplete state and older than 7 days.</p>
						</Grid>
						<Grid item xs={12} md={12} lg={12} style={{ background: '#003771', borderRadius: '4px', textAlign: 'left' }}>
							<p style={{ fontSize: '14px', float: 'left', marginLeft: '10px' }}><span style={{ fontSize: '1rem', fontWeight: 'bold', marginRight: 10 }}>Availability :</span>Total outage is calculated as sum of outage duration in minutes per technology from Outage table in the selected month. Availability is calculated as below.
								<br /><br />Total = Days in month * 24 (hours in day) * 60 (min in hour) * SOs count in technology
								Availability = Total - OutageInMin / Total * 100</p>
						</Grid>
					</Grid>
				</DialogueModal>
				<Stack direction="row"
					gap={1}
					alignItems="center"
					justifyContent="flex-end"
					width="100%"
					sx={{ mb: '10px', ...(toolbarSx || {}) }}
				>
					{(isDateRangeFilter || isChildren) && (
						<Stack
							direction={{ xs: 'column', sm: 'row' }}
							gap={2}
							alignItems={{ xs: 'flex-end', md: 'center' }}
							justifyContent={{ xs: 'flex-end' }}
							width={{ xs: '100%', md: 'auto' }}
							pr={{ xs: 1, md: 'initial' }}
							my={{ xs: 2, md: 0 }}
							{...childrenContainerProps}
						>
							{children}
						</Stack>
					)}
					{(metadataButtonLabel || onMetadataUpdatesClick) && (
					<Button
						variant="contained"
						onClick={onMetadataUpdatesClick}
						sx={{
							backgroundColor: 'rgba(0, 155, 216, 1)',
							color: 'white',
							fontWeight: 600,
							fontSize: '0.75rem',
							textTransform: 'uppercase',
							padding: '8px 16px',
							borderRadius: '4px',
							whiteSpace: 'nowrap',
							'&:hover': {
								backgroundColor: 'rgba(0, 135, 196, 1)',
							},
						}}
					>
						{metadataButtonLabel || 'Metadata Updates'}
					</Button>
				)}
					{
					isGroupBy && (<GroupByDropdown value={groupBy} onChange={onGroupByChange} groupByList={resolvedGroupByList} includeGroupByMenuColumns={includeGroupByMenuColumns}/>)
					}
					{!!isShowMoreButton && isShowMoreOption && !isFetching && (
						<MoreOptionDropdown
							menuItems={menuItems}
							isDisabled={isDisabled}
							isShowDisabledButton={isShowDisabledButton}
							iconColor={iconColor}
							icon={<MoreIconRounded style={{ width: '24px', height: '24px' }} />}
						/>
					)}
				</Stack>
			</Stack>
			{isFetching ? (
				<LoaderComponent />
			) : (
				<Box>
					<Box
						ref={gridViewportRef}
						sx={{
							'& .rdg': {
								background: 'transparent',
								border: 'none',
								overflowY: 'visible',
							},
							'.rdg-cell': {
								color: theme.palette.table.text,
								...(isEditing
									? { overflow: 'visible' }
									: {
										'&:hover': {
											textOverflow: 'clip',
											backgroundColor: 'transparent !important',
											overflowX: 'auto !important',
											overflowY: 'hidden !important',
											'&::-webkit-scrollbar': {
												height: '4px !important'
											}
										}
									})
							},
							'& .rdg .rdg-cell a': {
								color: theme.palette.table.text,
								textDecoration: 'none',
							},

							/* HOVER STATE FOR LINKS */
							'& .rdg .rdg-cell a:hover': {
								color: theme.palette.table.text,
								backgroundColor: 'transparent !important',
								textDecoration: 'underline',
							},
							'.rdg-cell-frozen[role="columnheader"]': {
								backgroundColor: `${theme.palette.table.headerBg} !important`,
								color: `${theme.palette.table.headerText} !important`,
							},
							'& .rdg-header-row .rdg-cell': {
								background: `${theme.palette.table.headerBg} !important`,
								color: `${theme.palette.table.headerText} !important`,
							},
							'& .mismatchClass .rdg-cell': {
								backgroundColor: '#f4d9a5ff !important',
								color: '#062242 !important'
							},
							'& .highlightClass .rdg-cell': {
								backgroundColor: '#062242 !important'
							},
							'& .highlightClass .rdg-cell-frozen': {
								backgroundColor: '#062242 !important'
							},
							'& .highlightClass-odd .rdg-cell': {
								backgroundColor: '#001E3E'
							},
							/* ODD ROW */
							'& .rdg-row:nth-of-type(odd)': {
								backgroundColor: `${theme.palette.table.oddRowBgColor} !important`,
							},
							/* EVEN ROW */
							'& .rdg-row:nth-of-type(even)': {
								backgroundColor: `${theme.palette.table.evenRowBgColor} !important`,
							},
							'&& .highlightClassProgram .rdg-cell': {
								backgroundColor: `${theme.palette.table.oddRowBgColor} !important`,
							},
							'&& .highlightClassProgram-odd .rdg-cell': {
								backgroundColor: `${theme.palette.table.evenRowBgColor} !important`,
							},
							'&& .totalRowClass .rdg-cell': {
								backgroundColor: `${theme.palette.table.highlightRowBgColor} !important`,
								color: `${theme.palette.table.text} !important`,
							}
						}}
					>
						<DataGrid
							ref={gridRef}
							key={windowSize}
							style={{
								height: '100%',
								maxHeight: 'calc(100vh - 250px)',
								width: '100%',
								minHeight: '100px',
								...style
							}}
							columns={draggableColumns}
							rows={sortedRows}
							rowHeight={rowHeight}
							headerRowHeight={headerRowHeight}
							defaultColumnOptions={{
								sortable: false,
								resizable: true
							}}
							sortColumns={sortColumns}
							onSortColumnsChange={setSortColumns}
							onRowsChange={handleEditCell}
							onCellClick={handleCellClick}
							rowClass={(row) => {
								const index = tableData.indexOf(row);

								const hasMismatch =
									(row.PepwiseSector?.trim() || '') !== (row.BusinessSector?.trim() || '') ||
									(row.PepwiseProject?.trim() || '') !== (row.ProjectName?.trim() || '') ||
									(row.PepwiseProgram?.trim() || '') !== (row.Program?.trim() || '') ||
									(row.PepwiseEnvironment?.trim() || '') !== (row.Environment?.trim() || '');

								// Selected row has highest priority
								if (selectedRowId && row.id === selectedRowId) {
									return 'selectedClass';
								}

								// Drifted rows
								if (row.columns_with_drift > 0) {
									return 'drifted';
								}

								// Total row
								if (row.id === 'total-row') {
									return 'totalRowClass';
								}

								// Child row logic
								if (childRowClass) {
									if (hasMismatch) {
										return 'mismatchClass';
									}

									const isEvenRow = index % 2 === 0;
									const isProgramTheme = isFinopsProgramsList || isLighterTheme;

									if (isProgramTheme) {
										return isEvenRow
											? 'highlightClassProgram'
											: 'highlightClassProgram-odd';
									}

									return isEvenRow
										? 'highlightClass'
										: 'highlightClass-odd';
								}

								return '';
							}
							}
							renderers={{ noRowsFallback: noRowsFallback ?? <EmptyRowsRenderer isFetching={isFetching} textColor={theme.palette.table.title} tableType = {tableType} /> }}
						/>
					</Box>
					{isRenderPagination && (
						<PaginationDetails
							page={page}
							pageSize={pageSize}
							paginationData={paginationData}
							listLength={listLength}
							isTotalCountLoading={isTotalCountLoading}
							tableType={tableType}
							handlepageSizeChange={handlepageSizeChange}
							handlePageChange={handlePageChange}
							showGoToPage={showGoToPage}
							pageSizeOptions={pageSizeOptions}
							isPrevNextPagination={isPrevNextPagination}
						/>
					)}
					<ColumnSelectorDialog
						open={dialogOpen}
						onClose={handleCloseColumnSelector}
						onCancel={handleCancelColumns}
						onSave={handleSaveColumns}
						onSelectAllChange={handleSelectAllChange}
						onColumnChange={handleColumnChange}
						columnNames={tableColumns.map((col: any) => col.name)}
						tempVisibleColumns={tempVisibleColumns}
						selectAll={selectAllColumns}
					/>
				</Box>
			)}
		</>
	);
}
